import React, { useEffect } from 'react';

import getConfig from 'next/config';
import router from 'next/router';

import { BlockText, EntityLabel, Text } from '@morressier/ts';

import { Grid } from '@material-ui/core';
import { FaRegCalendar, FaRegFileAlt } from 'react-icons/fa';

import AvatarBanner from 'components/AvatarBanner';
import StatsCard, { StatsContainer } from 'components/StatsCard';
import Markdown from 'components/Utilities/HTMLTextWithLink';
import { typedBoolean } from 'utils/array';
import { logEvent } from 'utils/eventTracking';

import { formatWebsiteShortNotation } from '../../utils/formatWebsiteShortNotation';
import * as Styles from '../EventHome/styles';
import * as ContainerStyles from '../styles';
import OrganizationEdit from './OrganizationEdit';
import SciMeetingsBanner from './SciMeetingsBanner';

const { publicRuntimeConfig } = getConfig();

// ID of ACS org, it has the same ID on staging and production
export const DPCorgId = '5eba80059652df329a983aeb';

type Props = {
  organization: Organization;
  organizationStats: OrganizationStats;
};
const defaultTheme = { banner_url: '', logo_url: '' };

const OrganizationHome = (props: Props) => {
  const { organization, organizationStats } = props;
  const { website } = organization;
  const { banner_url, logo_url } = organization.brand_theme ?? defaultTheme;

  useEffect(() => {
    logEvent('CL_VISITED');
    const { organization_id } = router.query;
    document.location.href = `${
      document.location.href.split('/o/')[0]
    }/cl/organizations/${organization_id}`;
  }, [props.organization.id]);

  return (
    <>
      <AvatarBanner bannerName={organization.name} bannerUrl={banner_url} avatarUrl={logo_url} />

      <ContainerStyles.PageContainer>
        <EntityLabel className="mt2 mb1">Organization</EntityLabel>
        <Styles.EventTitle className="mb1">{organization.name}</Styles.EventTitle>
        <OrganizationEdit organization={organization} />
        <Text
          href={website}
          textAs="a"
          size="body1_new"
          style={{ color: '#6600ff', fontWeight: 600, marginBottom: '8px' }}
          target="_blank"
        >
          {formatWebsiteShortNotation(website)}
        </Text>
        <BlockText color="grey" size="body1_new" className="mb1">
          {[organization.city, organization.country].filter(typedBoolean).join(', ')}
        </BlockText>
        <Grid container spacing={2}>
          <Grid item lg={9}>
            <Markdown>{organization.description}</Markdown>
          </Grid>
        </Grid>
        {publicRuntimeConfig.app_env === 'staging' && DPCorgId === organization.id && (
          <SciMeetingsBanner organizationId={organization.id} />
        )}
        <StatsContainer className="mt4">
          <StatsCard
            label="Conferences"
            icon={FaRegCalendar}
            stat={organizationStats.conferences}
            link={`/organizations/${organization.id}/events`}
          />
          <StatsCard
            label="Presentations"
            icon={FaRegFileAlt}
            stat={organizationStats.documents}
            link={`/organizations/${organization.id}/documents`}
          />
        </StatsContainer>
      </ContainerStyles.PageContainer>
    </>
  );
};

export default OrganizationHome;
